"use client"

import AdyenDropIn from "@/app/(site)/[locale]/checkout/centra/paymentMethods/AdyenDropIn"
import useSWRBasketSessionState from "@/app/(site)/_utils/_hooks/SWR/useSWRBasketSessionState"
import { scrollToElement } from "@/lib/utils/scroll"
import { t } from "@/translations"
import { useRef, useState } from "react"
import toast from "react-hot-toast"

// Soon these methods will be swapped out to new ones from Centra.
const AVAILABLE_PAYMENT_METHODS_COMPONENTS = {
    // QA
    "adyen-drop-in": AdyenDropIn,
    "adyen-drop-in-eu-en-test": AdyenDropIn,
    "adyen-drop-in-na-en-test": AdyenDropIn,
    // Prod
    "adyen-drop-in-eu-live": AdyenDropIn,
    "adyen-drop-in-na-live": AdyenDropIn,
    "adyen-drop-in-apac-live": AdyenDropIn,
    "adyen-drop-in-eu-ja-live": AdyenDropIn,
    "adyen-drop-in-eu-fr-live": AdyenDropIn,
    "adyen-drop-in-eu-de-live": AdyenDropIn,
    "adyen-drop-in-na-fr-live": AdyenDropIn

    //Prod New Methods (Not yet implemented)
    // "adyen-drop-in-apac-live": AdyenDropIn,
    // "adyen-drop-in-eu-live": AdyenDropIn,
    // "adyen-drop-in-na-live": AdyenDropIn,
    // Add more payment methods here as needed
} as const

const getPaymentMethodComponent = (paymentMethod: string) => {
    if (paymentMethod.startsWith("adyen-drop-in")) {
        return AdyenDropIn
    }
    return AVAILABLE_PAYMENT_METHODS_COMPONENTS[
        paymentMethod as keyof typeof AVAILABLE_PAYMENT_METHODS_COMPONENTS
    ]
}

export type PaymentMethodErrorCallback = ({ message }: { message: string }) => void

type PaymentProps = {
    langCode: string
}

export default function PaymentNew({ langCode }: PaymentProps) {
    const [reload, setReload] = useState(false)
    const paymentReference = useRef<HTMLDivElement>(null)
    const loopPrevention = useRef(false)

    const { selection, isLoadingSelection } = useSWRBasketSessionState()
    const currentPaymentMethod = selection?.paymentMethod || "adyen-drop-in"

    const forcePaymentReload = (): void => {
        if (!loopPrevention.current) {
            loopPrevention.current = true

            // Force reload of the payment method
            setReload(true)
            const timeout = setTimeout(() => {
                setReload(false)
            }, 250)

            // Prevent the reload from happening too often
            const loopTimeout = setTimeout(() => {
                loopPrevention.current = false
            }, 1000)

            // Store cleanup function for potential future use
            // Note: Currently not used, but kept for potential cleanup needs
            const cleanup = () => {
                clearTimeout(timeout)
                clearTimeout(loopTimeout)
            }
            // Cleanup is stored but not returned since function is void
            void cleanup
        }
    }

    const handleError: PaymentMethodErrorCallback = ({ message }) => {
        let toastMessage: string

        if (message.includes("CVC Declined")) {
            toastMessage = t(langCode, "checkout.payment_errors.payment_cvc_declined")
        } else if (message === "Selection error") {
            toastMessage = t(langCode, "checkout.payment_errors.payment_selection_error")
        } else {
            toastMessage = t(langCode, "checkout.payment_errors.payment_generic_error")
        }

        toast.error(toastMessage, {
            duration: 6000,
            id: "payment-error",
            className: "!text-col-text-alert",
            icon: null,
            position: "bottom-left"
        })
        forcePaymentReload()
    }

    const handleLoaded = () => {
        if (paymentReference.current) {
            setTimeout(() => {
                scrollToElement(paymentReference.current, {
                    smooth: true,
                    yOffset: -150
                })
            }, 100)
        }
    }

    // If we're reloading, don't render the payment widget
    if (reload) {
        return null
    }

    const PaymentMethodComponent = getPaymentMethodComponent(currentPaymentMethod)
    if (!PaymentMethodComponent) {
        return (
            <p className="text-col-ui-secondary">
                {t(langCode, "checkout.no_payment_methods_available")}
            </p>
        )
    }

    const PaymentMethod = PaymentMethodComponent

    return (
        <div className="relative pb-32" ref={paymentReference}>
            {isLoadingSelection && (
                <div className="absolute inset-0 z-[999] flex items-center justify-center bg-white/50">
                    <div className="h-40 w-40 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
                </div>
            )}
            <div
                className="min-h-[400px]"
                style={{
                    opacity: isLoadingSelection ? 0.5 : 1,
                    pointerEvents: isLoadingSelection ? "none" : "auto"
                }}
            >
                <PaymentMethod
                    onError={handleError}
                    onLoaded={handleLoaded}
                    langCode={langCode}
                />
            </div>
        </div>
    )
}
